import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as lifeplanApi from '../../services/lifeplan-api.js';
import type { LifeplanConfig } from '../../services/lifeplan-api.js';

@customElement('cp-lifeplan-settings')
export class CpLifeplanSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _config: LifeplanConfig | null = null;
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _filePath = '';
  @state() private _connecting = false;
  @state() private _reparsing = false;
  @state() private _successMessage = '';

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadConfig();
  }

  private async _loadConfig(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const result = await lifeplanApi.getConfig();
      if ('isConfigured' in result && !result.isConfigured) {
        this._config = null;
      } else {
        this._config = result as LifeplanConfig;
        this._filePath = this._config.file_path;
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private _onPathInput(e: Event): void {
    this._filePath = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private async _connect(): Promise<void> {
    const path = this._filePath.trim();
    if (!path || this._connecting) return;

    this._connecting = true;
    this._error = '';
    this._successMessage = '';
    try {
      this._config = await lifeplanApi.configure(path);
      this._successMessage = 'Lebensplan erfolgreich verbunden';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Verbinden';
    } finally {
      this._connecting = false;
    }
  }

  private async _reparse(): Promise<void> {
    if (this._reparsing) return;

    this._reparsing = true;
    this._error = '';
    this._successMessage = '';
    try {
      await lifeplanApi.reparse();
      await this._loadConfig();
      this._successMessage = 'Lebensplan erfolgreich neu geparst';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Parsen';
    } finally {
      this._reparsing = false;
    }
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      void this._connect();
    }
  }

  private _formatDate(dateStr: string | null): string {
    if (!dateStr) return 'Nie';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private _renderStatus() {
    if (!this._config) {
      return html`<span class="cp-lp__status cp-lp__status--disconnected">Nicht verbunden</span>`;
    }
    if (this._config.status === 'connected') {
      return html`<span class="cp-lp__status cp-lp__status--connected">Verbunden</span>`;
    }
    if (this._config.status === 'error') {
      return html`<span class="cp-lp__status cp-lp__status--error">Fehler</span>`;
    }
    return html`<span class="cp-lp__status cp-lp__status--disconnected">Nicht verbunden</span>`;
  }

  render() {
    return html`
      <div class="cp-lp">
        <div class="cp-lp__header">
          <h3 class="cp-lp__title">Lebensplan</h3>
          ${this._renderStatus()}
        </div>

        ${this._error ? html`
          <div class="cp-lp__error">${this._error}</div>
        ` : nothing}

        ${this._successMessage ? html`
          <div class="cp-lp__success">${this._successMessage}</div>
        ` : nothing}

        ${this._loading ? html`
          <p class="cp-lp__loading">Konfiguration wird geladen...</p>
        ` : html`
          <div class="cp-lp__form">
            <label class="cp-lp__label" for="lifeplan-path">Dateipfad zur Lebensplan-Markdown-Datei</label>
            <div class="cp-lp__input-row">
              <input
                id="lifeplan-path"
                class="cp-lp__input"
                type="text"
                placeholder="/Users/michael/lebensplan.md"
                .value=${this._filePath}
                @input=${this._onPathInput}
                @keydown=${this._onKeyDown}
              />
              <button
                class="cp-lp__btn cp-lp__btn--connect"
                @click=${this._connect}
                ?disabled=${this._connecting || !this._filePath.trim()}
              >${this._connecting ? 'Verbinde...' : 'Verbinden'}</button>
            </div>
          </div>

          ${this._config && this._config.status === 'connected' ? html`
            <div class="cp-lp__info">
              <div class="cp-lp__info-row">
                <span class="cp-lp__info-label">Letztes Parsing:</span>
                <span class="cp-lp__info-value">${this._formatDate(this._config.last_parsed_at)}</span>
              </div>
              <button
                class="cp-lp__btn cp-lp__btn--reparse"
                @click=${this._reparse}
                ?disabled=${this._reparsing}
              >${this._reparsing ? 'Parst...' : 'Neu Parsen'}</button>
            </div>
          ` : nothing}

          ${this._config?.last_error ? html`
            <div class="cp-lp__error">${this._config.last_error}</div>
          ` : nothing}
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-lifeplan-settings': CpLifeplanSettings;
  }
}
